import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, ExternalLink, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { api } from '../../services/api';
import type { PublicShareRow, StorageHealthFlag, StorageVisibilitySummary } from '../../services/types';

function formatBytes(bytes?: number | null) {
    const value = Number(bytes || 0);
    if (value <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let size = value;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex += 1;
    }
    return `${size.toFixed(size >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function formatDate(value?: string | null) {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
}

function statusLabel(status: PublicShareRow['status']) {
    if (status === 'missing_file') return 'Missing File';
    return status.charAt(0).toUpperCase() + status.slice(1);
}

function statusClass(status: PublicShareRow['status']) {
    if (status === 'active') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
    if (status === 'expired') return 'border-amber-200 bg-amber-50 text-amber-700';
    if (status === 'revoked') return 'border-slate-200 bg-slate-100 text-slate-700';
    return 'border-red-200 bg-red-50 text-red-700';
}

function MetricCard({ label, value, detail }: { label: string; value: string; detail: string }) {
    return (
        <Card>
            <CardContent className="py-4">
                <div className="text-xs font-medium uppercase text-slate-500">{label}</div>
                <div className="mt-1 text-2xl font-semibold text-slate-900">{value}</div>
                <div className="mt-1 text-xs text-slate-500">{detail}</div>
            </CardContent>
        </Card>
    );
}

function HealthRow({ flag }: { flag: StorageHealthFlag }) {
    const isWarning = flag.severity === 'warning' || flag.severity === 'danger';
    const Icon = isWarning ? AlertTriangle : CheckCircle2;
    return (
        <div className={`flex items-start gap-2 rounded border p-3 text-sm ${isWarning ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-slate-200 bg-slate-50 text-slate-700'}`}>
            <Icon className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="min-w-0">
                <div className="font-medium">{flag.message}</div>
                <div className="text-xs opacity-80">
                    Count {flag.count}
                    {typeof flag.size_bytes === 'number' ? `, ${formatBytes(flag.size_bytes)}` : ''}
                </div>
            </div>
        </div>
    );
}

export function StorageVisibilityTab() {
    const [summary, setSummary] = useState<StorageVisibilitySummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadSummary = async () => {
        try {
            setLoading(true);
            setError(null);
            setSummary(await api.settings.getStorageVisibilitySummary());
        } catch (err: any) {
            setError(err?.message || 'Failed to load storage and sharing summary');
            setSummary(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadSummary();
    }, []);

    const healthFlags = useMemo(() => Object.values(summary?.health_flags || {}), [summary]);

    if (loading && !summary) {
        return (
            <Card>
                <CardContent className="py-10 text-sm text-slate-500">Loading storage visibility...</CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Card>
                <CardContent className="py-6 space-y-3">
                    <div className="text-sm text-red-700">{error}</div>
                    <Button type="button" variant="outline" size="sm" onClick={loadSummary}>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Retry
                    </Button>
                </CardContent>
            </Card>
        );
    }

    if (!summary) return null;

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-end">
                <Button type="button" variant="outline" size="sm" disabled={loading} onClick={loadSummary}>
                    <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                </Button>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
                <MetricCard
                    label="Database"
                    value={formatBytes(summary.database.size_bytes + summary.database.wal_size_bytes)}
                    detail={`${summary.database.file_name}${summary.database.wal_active ? ' with WAL active' : ''}`}
                />
                <MetricCard
                    label="Uploads"
                    value={formatBytes(summary.uploads.recorded_active_size_bytes)}
                    detail={`${summary.uploads.recorded_active_count} recorded, ${summary.uploads.missing_file_count} missing`}
                />
                <MetricCard
                    label="Public Links"
                    value={String(summary.public_links.active)}
                    detail={`${summary.public_links.total} total, ${summary.public_links.expired} expired`}
                />
                <MetricCard
                    label="Backups"
                    value={formatBytes(summary.backups.size_bytes)}
                    detail={`${summary.backups.file_count} files in ${summary.backups.base_label}`}
                />
                <MetricCard
                    label="Correction Backups"
                    value={formatBytes(summary.correction_backups.size_bytes)}
                    detail={`${summary.correction_backups.file_count}/${summary.correction_backups.max_files} files · ${summary.correction_backups.retention_days}-day retention`}
                />
            </div>

            <Card>
                <CardContent className="py-4">
                    <div className="mb-3 text-sm font-semibold text-slate-800">External Visibility</div>
                    {summary.public_shares.length === 0 ? (
                        <div className="rounded border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                            No public document share links found.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table aria-label="External Visibility" className="min-w-full text-sm">
                                <thead className="border-b text-left text-xs uppercase text-slate-500">
                                    <tr>
                                        <th className="px-2 py-2">Status</th>
                                        <th className="px-2 py-2">Document</th>
                                        <th className="px-2 py-2">Attachment</th>
                                        <th className="px-2 py-2">Expires</th>
                                        <th className="px-2 py-2">Last Access</th>
                                        <th className="px-2 py-2">Size</th>
                                        <th className="px-2 py-2">Token</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y">
                                    {summary.public_shares.map((share) => (
                                        <tr key={share.share_id}>
                                            <td className="px-2 py-2">
                                                <span className={`inline-flex rounded border px-2 py-0.5 text-xs font-medium ${statusClass(share.status)}`}>
                                                    {statusLabel(share.status)}
                                                </span>
                                            </td>
                                            <td className="px-2 py-2 text-slate-700">
                                                {share.document_type} #{share.document_id}
                                            </td>
                                            <td className="px-2 py-2">
                                                <div className="font-medium text-slate-800">{share.attachment_name}</div>
                                                <div className="text-xs text-slate-500">{share.file_label || '-'}</div>
                                            </td>
                                            <td className="px-2 py-2 text-slate-600">{formatDate(share.expires_at)}</td>
                                            <td className="px-2 py-2 text-slate-600">{formatDate(share.last_accessed_at)}</td>
                                            <td className="px-2 py-2 text-slate-600">{formatBytes(share.file_size_bytes)}</td>
                                            <td className="px-2 py-2 text-slate-600">
                                                <div className="flex items-center gap-2">
                                                    <span>{share.token_masked || '-'}</span>
                                                    {share.public_url && (
                                                        <a href={share.public_url} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline" aria-label={`Open ${share.attachment_name}`}>
                                                            <ExternalLink className="h-4 w-4" />
                                                        </a>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <Card>
                    <CardContent className="py-4">
                        <div className="mb-3 text-sm font-semibold text-slate-800">Storage Breakdown</div>
                        <div className="overflow-x-auto">
                            <table aria-label="Storage Breakdown" className="min-w-full text-sm">
                                <thead className="border-b text-left text-xs uppercase text-slate-500">
                                    <tr>
                                        <th className="px-2 py-2">Area</th>
                                        <th className="px-2 py-2">Recorded</th>
                                        <th className="px-2 py-2">On Disk</th>
                                        <th className="px-2 py-2">Missing</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y">
                                    {summary.storage_breakdown.map((row) => (
                                        <tr key={`${row.area}-${row.category}`}>
                                            <td className="px-2 py-2">
                                                <div className="font-medium text-slate-800">{row.area}</div>
                                                <div className="text-xs text-slate-500">{row.category}</div>
                                            </td>
                                            <td className="px-2 py-2 text-slate-600">{row.recorded_count} / {formatBytes(row.recorded_size_bytes)}</td>
                                            <td className="px-2 py-2 text-slate-600">{row.present_count} / {formatBytes(row.present_size_bytes)}</td>
                                            <td className="px-2 py-2 text-slate-600">{row.missing_count}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="py-4">
                        <div className="mb-3 text-sm font-semibold text-slate-800">Largest Files</div>
                        {summary.largest_documents.length === 0 ? (
                            <div className="rounded border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">No uploaded document records found.</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table aria-label="Largest Files" className="min-w-full text-sm">
                                    <thead className="border-b text-left text-xs uppercase text-slate-500">
                                        <tr>
                                            <th className="px-2 py-2">File</th>
                                            <th className="px-2 py-2">Entity</th>
                                            <th className="px-2 py-2">Recorded</th>
                                            <th className="px-2 py-2">Disk</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y">
                                        {summary.largest_documents.map((doc) => (
                                            <tr key={doc.doc_id}>
                                                <td className="px-2 py-2">
                                                    <div className="font-medium text-slate-800">{doc.file_name}</div>
                                                    <div className="text-xs text-slate-500">
                                                        {doc.file_exists ? 'Present' : 'Missing'}{doc.is_deleted ? ' / Deleted' : ''}
                                                    </div>
                                                </td>
                                                <td className="px-2 py-2 text-slate-600">{doc.entity_type} #{doc.entity_id}</td>
                                                <td className="px-2 py-2 text-slate-600">{formatBytes(doc.recorded_size_bytes)}</td>
                                                <td className="px-2 py-2 text-slate-600">{formatBytes(doc.filesystem_size_bytes)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardContent className="py-4">
                    <div className="mb-3 text-sm font-semibold text-slate-800">Storage Health</div>
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                        {healthFlags.map((flag, index) => (
                            <HealthRow key={`${flag.message}-${index}`} flag={flag} />
                        ))}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
